/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef } from 'react'
import { Helmet } from 'react-helmet'
import utils from '../utils'
import ContactCTA from '../components/services/ContactCTA'

function ContactSection({ emailRef, subjectRef, textRef, submitHandler }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-24 items-start px-8">
            <section className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB]/30">
                <h2 className="text-2xl font-headline text-primary mb-3">Send a Message</h2>
                <p className="text-lg text-on-surface-variant font-light leading-relaxed mb-8">
                    Whether you need furnished accommodation in Kisumu, corporate retreat planning, or bespoke catering, we are here to help. 
                    Contact us for reservations or concierge support; we respond within 24 hours.
                </p>
                <form onSubmit={submitHandler} className="space-y-8">
                    <div className="space-y-1">
                        <label
                            className="text-[10px] uppercase tracking-[0.2em] text-[#6B7C76] font-medium block mb-1"
                            htmlFor="name"
                        >
                            Your Name
                        </label>
                        <input
                            className="w-full bg-transparent border-0 border-b border-[#E5E7EB] py-2 px-0 text-[#0F1715] focus:border-[#E87A25] focus:ring-0 text-base transition-all placeholder-[#6B7C76]/30 outline-none"
                            id="name"
                            name="name"
                            placeholder="John Doe"
                            type="text"
                        />
                    </div>
                    <div className="space-y-1">
                        <label
                            className="text-[10px] uppercase tracking-[0.2em] text-[#6B7C76] font-medium block mb-1"
                            htmlFor="email"
                        >
                            Email Address*
                        </label>
                        <input
                            type="email"
                            required
                            ref={emailRef}
                            name="email"
                            id="email"
                            placeholder="hello@example.com"
                            className="w-full bg-transparent border-0 border-b border-[#E5E7EB] py-2 px-0 text-[#0F1715] focus:border-[#E87A25] focus:ring-0 text-base transition-all placeholder-[#6B7C76]/30 outline-none"
                        />
                    </div>
                    <div className="space-y-1">
                        <label
                            className="text-[10px] uppercase tracking-[0.2em] text-[#6B7C76] font-medium block mb-1"
                            htmlFor="subject"
                        >
                            Subject
                        </label>
                        <input
                            type="text"
                            ref={subjectRef}
                            name="subject"
                            id="subject"
                            placeholder="How can we help?"
                            className="w-full bg-transparent border-0 border-b border-[#E5E7EB] py-2 px-0 text-[#0F1715] focus:border-[#E87A25] focus:ring-0 text-base transition-all placeholder-[#6B7C76]/30 outline-none"
                        />
                    </div>
                    <div className="space-y-1">
                        <label
                            className="text-[10px] uppercase tracking-[0.2em] text-[#6B7C76] font-medium block mb-1"
                            htmlFor="message"
                        >
                            Message*
                        </label>
                        <textarea
                            ref={textRef}
                            name="message"
                            id="message"
                            className="w-full bg-transparent border-0 border-b border-[#E5E7EB] py-2 px-0 text-[#0F1715] focus:border-[#E87A25] focus:ring-0 text-base transition-all resize-none placeholder-[#6B7C76]/30 outline-none"
                            placeholder="Tell us about your stay or event..."
                            rows="4"
                            required
                        />
                    </div>

                    <div className="pt-4">
                        <button
                            className="bg-[#1B4D3E] text-white w-full py-4 rounded-xl text-xs uppercase tracking-widest font-bold hover:bg-[#143a2f] group transition-colors flex justify-center items-center gap-2 active:scale-[0.98]"
                            type="submit"
                        >
                            Submit Request
                            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </button>
                    </div>
                </form>
            </section>

            <section className="space-y-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div className="group">
                        <p className="text-[10px] uppercase tracking-widest text-[#E87A25] font-medium mb-3">Direct Line</p>
                        <a
                            className="text-xl font-bold text-primary font-headline hover:text-[#E87A25] transition-colors"
                            href="tel:+254711610000"
                        >
                            +254 711 610 000
                        </a>
                    </div>
                    <div className="group">
                        <p className="text-[10px] uppercase tracking-widest text-[#E87A25] font-medium mb-3">Inquiries</p>
                        <a
                            className="text-xl text-primary font-headline font-bold hover:text-[#E87A25] transition-colors"
                            href="mailto:sagecreations@gmail.com"
                        >
                            hello@sagecreations.com
                        </a>
                    </div>
                    <div className="group sm:col-span-2">
                        <p className="text-[10px] uppercase tracking-widest text-[#E87A25] font-medium mb-3">Location</p>
                        <p className="text-xl font-bold text-primary font-headline leading-relaxed">
                            Milimani Estate, Lake View Heights<br />
                            Kisumu City, Kenya
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    <p className="text-[10px] uppercase tracking-widest text-[#E87A25] font-medium">Find us here</p>
                    <div className="rounded-2xl overflow-hidden aspect-video relative grayscale hover:grayscale-0 transition-all duration-700 cursor-pointer shadow-md group">
                        <img
                            alt="Map of Sage Apartments location in Kisumu"
                            className="w-full h-full object-cover"
                            src="https://lh3.googleusercontent.com/aida-public/AB6AXuAPy9Na2x7VkG1VVNAfKY-MWkHKtJSxPPD9Ahki1deFE4mK3_3X3bjour-NhINB2uKtTQClBjmrL8pqwIVMj-g_16bMTvexVpZvquIJru6hEUbYocRzozOvX8iF_Q59TmqingzEsU-KaA2Ay75jn4kyLr-N2eogKUziNF1F307Uf0GJFbV2adKZv742rVHilfv3KYlljPdr1kJQXhLffYgxRry-T9T0mIhdIKIUi6NmwfBlA9yZkJc8sQGeqxER5lMQEytIuvm7LjU"
                        />
                        <div className="absolute inset-0 bg-[#1B4D3E]/5 group-hover:bg-transparent transition-colors"></div>
                    </div>
                </div>

                <div className="flex gap-4 pt-4">
                    <a
                        className="w-10 h-10 flex items-center justify-center rounded-full border border-[#E5E7EB] text-[#0F1715] hover:bg-[#1B4D3E] hover:text-white transition-all"
                        href="#"
                        aria-label="Share location"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                    </a>
                    <a
                        className="w-10 h-10 flex items-center justify-center rounded-full border border-[#E5E7EB] text-[#0F1715] hover:bg-[#1B4D3E] hover:text-white transition-all"
                        href="https://maps.google.com/?q=Sage+Furnished+Apartments+Kisumu"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open in Google Maps"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                    </a>
                </div>
            </section>
        </div>
    )
}

function ContactPage() {
    const url = window.location.href
    const emailRef = useRef()
    const subjectRef = useRef()
    const textRef = useRef()

    function mailTo(body, subject) {
        const destinationEmail = "hello@sagecreations.co.ke"
        window.location.href = `mailto:${destinationEmail}?subject=${subject}&body=${body}`;
        emailRef.current.value = ''
        subjectRef.current.value = ''
        textRef.current.value = ''
    }
    function submitHandler(e) {
        e.preventDefault()
        const email = emailRef.current.value
        const subject = subjectRef.current.value
        const text = textRef.current.value
        const body = `from: ${email}  content: ${text}`
        mailTo(body, subject)
    }
    return (
        <div>
            <Helmet>
                <title>Contact Us - {utils.siteName}</title>
                <link rel="canonical" href={url} />
            </Helmet>
            <div className='bg-primary/5'>
                <header className="px-8 mb-16 max-w-7xl mx-auto py-3">
                    <span className="block text-xs uppercase tracking-widest font-label text-secondary mb-4">Connect with Us</span>
                    <h1 className="text-3xl md:text-5xl font-headline text-primary max-w-3xl leading-tight">Contact Us</h1>
                    <div className="w-24 h-1 bg-secondary mt-5"></div>
                </header>
            </div>
            <div className=" max-w-7xl mx-auto space-y-10 mb-16">
                <ContactSection
                    emailRef={emailRef}
                    subjectRef={subjectRef}
                    textRef={textRef}
                    submitHandler={submitHandler}
                />
            </div>
            <ContactCTA />
        </div>
    )
}

export default ContactPage